import React from 'react';
import {
  DATASOURCE_KEY_MAP,
  MSCIENCE_METRIC_KEY_MAP,
  GSD_METRIC_KEY_MAP,
  NPD_METRIC_KEY_MAP,
  NEWZOO_METRIC_KEY_MAP,
  GAMALYTIC_METRIC_KEY_MAP,
} from './keyMap';
import { COUNTRY_EMOJI_GLOBAL, COUNTRY_IMG_US } from '@intelligence/v3/constants/region';

import type { IMetricExplanationTipsClickHandler } from '@intelligence/v3/components/MetricExplanationTips';

const USCountryImg = <img style={{ width: 18, height: 12, verticalAlign: 'middle' }} src={COUNTRY_IMG_US} alt="" />;

/**
 * combine平台信息配置
 */
export const COMBINE_PLATFORM: IDBIPlatform = {
  platform: 'combine',
  platformName: {
    en: 'PC & Console',
    cn: '端游 & 主机游戏',
  },
};

/**
 * combine平台数据源信息配置
 */
export const COMBINE_DATA_SOURCES_MAPPING: IRecord<IDBIDataSource> = {
  [DATASOURCE_KEY_MAP.MSCIENCE]: {
    dataSource: DATASOURCE_KEY_MAP.MSCIENCE,
    dataSourceJump: 'MScience',
    dataSourceName: {
      en: 'Mscience',
      cn: 'Mscience',
    },
  },
  [DATASOURCE_KEY_MAP.GSD]: {
    dataSource: DATASOURCE_KEY_MAP.GSD,
    dataSourceJump: 'Sparkers(GSD)',
    dataSourceName: {
      en: 'GSD',
      cn: 'GSD',
    },
  },
  [DATASOURCE_KEY_MAP.NPD]: {
    dataSource: DATASOURCE_KEY_MAP.NPD,
    dataSourceJump: 'NPD',
    dataSourceName: {
      en: 'NPD',
      cn: 'NPD',
    },
  },
  [DATASOURCE_KEY_MAP.NEWZOO]: {
    dataSource: DATASOURCE_KEY_MAP.NEWZOO,
    dataSourceJump: 'Newzoo',
    dataSourceName: {
      en: 'Newzoo',
      cn: 'Newzoo',
    },
  },
  [DATASOURCE_KEY_MAP.GAMALYTIC]: {
    dataSource: DATASOURCE_KEY_MAP.GAMALYTIC,
    dataSourceJump: 'Gamalytic',
    dataSourceName: {
      en: 'Gamalytic',
      cn: 'Gamalytic',
    },
  },
};

/**
 * MScience数据源通用指标更新时间（英文）
 */
const COMMON_MSCIENCE_UPDATE_TIME_EN =
  '6-day delay (US), 7-14 day delay (UK, France, Germany, Spain). Data up to 6 days prior (US) and 7-14 days prior (Europe) can be viewed daily at 4:40 PM UTC+0.';

/**
 * MScience数据源通用指标更新时间（中文）
 */
const COMMON_MSCIENCE_UPDATE_TIME_CN =
  '延时6天（美国），延时7-14天（英、法、德、西）, 每天的4:40PM UTC+0 可以看到日期截止6天前的数据（美国）和日期截止7-14天前的数据 （欧洲）。';

const MSCIENCE_DATASOURCE_HIGH_CONFIDENCE_IN_US_EN = (
  <b>M Science mainly covers {USCountryImg} US data, with high confidence in US data.</b>
);

const MSCIENCE_DATASOURCE_HIGH_CONFIDENCE_IN_US_CN = (
  <b>M Science主要覆盖 {USCountryImg} 美国数据，美国数据置信度高。</b>
);

const MSCIENCE_DATASOURCE_LOWER_CONFIDENCE_IN_EUROPEAN_EN =
  'Some games include data from the UK, France, Germany, and Spain, with lower confidence in European data.';

const MSCIENCE_DATASOURCE_LOWER_CONFIDENCE_IN_EUROPEAN_CN =
  '部分游戏含有英、法、德、西四个国家数据，欧洲数据置信度较低。';

const MSCIENCE_DATASOURCE_CONFIDENCE_IN_EUROPEAN_EN = (
  <>
    {MSCIENCE_DATASOURCE_HIGH_CONFIDENCE_IN_US_EN} {MSCIENCE_DATASOURCE_LOWER_CONFIDENCE_IN_EUROPEAN_EN}
  </>
);

const MSCIENCE_DATASOURCE_CONFIDENCE_IN_EUROPEAN_CN = (
  <>
    {MSCIENCE_DATASOURCE_HIGH_CONFIDENCE_IN_US_CN}
    {MSCIENCE_DATASOURCE_LOWER_CONFIDENCE_IN_EUROPEAN_CN}
  </>
);

const renderMScienceDataSourceCommonInfoEN = (onClick?: IMetricExplanationTipsClickHandler) => (
  <div>
    {MSCIENCE_DATASOURCE_CONFIDENCE_IN_EUROPEAN_EN}
    <b>
      {COUNTRY_EMOJI_GLOBAL} Global data is aggregated data from these five countries. It&apos;s recommended to
      <span
        className="dbi-highlight-color dbi-text-decoration-underline dbi-cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          onClick?.('dataSource', { key: 'setRegionUSOnly' });
        }}
      >
        &nbsp;filter for US&nbsp;
      </span>
      data only for reference.
    </b>
  </div>
);

const renderMScienceDataSourceCommonInfoCN = (onClick?: IMetricExplanationTipsClickHandler) => (
  <div>
    {MSCIENCE_DATASOURCE_CONFIDENCE_IN_EUROPEAN_CN}
    <b>
      {COUNTRY_EMOJI_GLOBAL} 全球数据为五国加总数据，推荐
      <span
        className="dbi-highlight-color dbi-text-decoration-underline dbi-cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          onClick?.('dataSource', { key: 'setRegionUSOnly' });
        }}
      >
        筛选仅美国
      </span>
      数据用于参考。
    </b>
  </div>
);

const renderMScienceDataSourceASTInfoEN = (onClick?: IMetricExplanationTipsClickHandler) => (
  <div>
    {MSCIENCE_DATASOURCE_CONFIDENCE_IN_EUROPEAN_EN}
    <b>
      {COUNTRY_EMOJI_GLOBAL} Global data is weighted average data from these five countries. It&apos;s recommended to
      <span
        className="dbi-highlight-color dbi-text-decoration-underline dbi-cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          onClick?.('dataSource', { key: 'setRegionUSOnly' });
        }}
      >
        &nbsp;filter for US&nbsp;
      </span>
      data only for reference.
    </b>
  </div>
);

const renderMScienceDataSourceASTInfoCN = (onClick?: IMetricExplanationTipsClickHandler) => (
  <div>
    {MSCIENCE_DATASOURCE_CONFIDENCE_IN_EUROPEAN_CN}
    <b>
      {COUNTRY_EMOJI_GLOBAL} 全球数据为五国加权平均数据，推荐
      <span
        className="dbi-highlight-color dbi-text-decoration-underline dbi-cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          onClick?.('dataSource', { key: 'setRegionUSOnly' });
        }}
      >
        筛选仅美国
      </span>
      数据用于参考。
    </b>
  </div>
);

/**
 * combine平台数据源指标配置
 */
export const COMBINE_DATA_SOURCES_METRICS_MAPPING: IRecord<IRecord<IDBIMetric>> = {
  [DATASOURCE_KEY_MAP.MSCIENCE]: {
    [MSCIENCE_METRIC_KEY_MAP.DIGITAL_REVENUE]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.MSCIENCE],
      metric: 'digital_revenue',
      metricJump: 'Digital Revenue',
      metricName: {
        en: 'Digital Revenue($)',
        cn: '线上收入($)',
      },
      explanation: {
        en: (props: { onClick?: IMetricExplanationTipsClickHandler }) => {
          const { onClick } = props;
          return {
            definition: 'Game revenue made through online purchase, including base game, MTX, pre-orders, and DLC.',
            dataSource: renderMScienceDataSourceCommonInfoEN(onClick),
            granularity: 'Daily',
            aggregation: 'Total value',
            updateTime: COMMON_MSCIENCE_UPDATE_TIME_EN,
          };
        },
        cn: (props: { onClick?: IMetricExplanationTipsClickHandler }) => {
          const { onClick } = props;
          return {
            definition: '游戏线上收入, 包含电子版游戏拷贝、游戏内交易、预购、DLC。',
            dataSource: renderMScienceDataSourceCommonInfoCN(onClick),
            granularity: '日',
            aggregation: '取总值',
            updateTime: COMMON_MSCIENCE_UPDATE_TIME_CN,
          };
        },
      },
    },
    [MSCIENCE_METRIC_KEY_MAP.DIGITAL_UNITS]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.MSCIENCE],
      metric: 'digital_units',
      metricJump: 'Digital Units',
      metricName: {
        en: 'Digital Units',
        cn: '线上销量',
      },
      explanation: {
        en: (props: { onClick?: IMetricExplanationTipsClickHandler }) => {
          const { onClick } = props;
          return {
            definition: 'Game units made through online purchase, only including base game.',
            dataSource: renderMScienceDataSourceCommonInfoEN(onClick),
            granularity: 'Daily',
            aggregation: 'Total value',
            updateTime: COMMON_MSCIENCE_UPDATE_TIME_EN,
          };
        },
        cn: (props: { onClick?: IMetricExplanationTipsClickHandler }) => {
          const { onClick } = props;
          return {
            definition: '游戏线上销量，仅包含电子版游戏本体销量。',
            dataSource: renderMScienceDataSourceCommonInfoCN(onClick),
            granularity: '日',
            aggregation: '取总值',
            updateTime: COMMON_MSCIENCE_UPDATE_TIME_CN,
          };
        },
      },
    },
    [MSCIENCE_METRIC_KEY_MAP.DIGITAL_ARPPU]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.MSCIENCE],
      metric: 'digital_units',
      metricJump: 'Digital Units',
      metricName: {
        en: 'Digital Units',
        cn: '线上销量',
      },
      explanation: {
        en: {
          definition: 'Digital Average Revenue Per Paying User($).',
          dataSource: <div>{MSCIENCE_DATASOURCE_HIGH_CONFIDENCE_IN_US_EN} This metric only has US data.</div>,
          granularity: 'Daily',
          aggregation: 'Weighted average value',
          updateTime: '6-day delay (US). Data up to 6 days prior (US) can be viewed daily at 4:40 PM UTC+0.',
        },
        cn: {
          definition: 'Digital ARPPU($)(US only) - 付费玩家人均线上消费额(美元)(仅美国)',
          dataSource: <div>{MSCIENCE_DATASOURCE_HIGH_CONFIDENCE_IN_US_CN}此指标仅美国数据。</div>,
          granularity: '日',
          aggregation: '取加权均值',
          updateTime: '延时6天（美国）,每天的4:40PM UTC+0 可以看到日期截止6天前的数据（美国）',
        },
      },
    },
    [MSCIENCE_METRIC_KEY_MAP.DIGITAL_AST]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.MSCIENCE],
      metric: 'digital_units',
      metricJump: 'Digital Units',
      metricName: {
        en: 'Digital Units',
        cn: '线上销量',
      },
      explanation: {
        en: (props: { onClick?: IMetricExplanationTipsClickHandler }) => {
          const { onClick } = props;
          return {
            definition: 'Digital Average Spending per Transaction($).',
            dataSource: renderMScienceDataSourceASTInfoEN(onClick),
            granularity: 'Daily',
            aggregation: 'Weighted average value',
            updateTime: COMMON_MSCIENCE_UPDATE_TIME_EN,
          };
        },
        cn: (props: { onClick?: IMetricExplanationTipsClickHandler }) => {
          const { onClick } = props;
          return {
            definition: 'Digital AST($) - 线上平均单笔交易消费额(美元)',
            dataSource: renderMScienceDataSourceASTInfoCN(onClick),
            granularity: '日',
            aggregation: '取加权均值',
            updateTime: COMMON_MSCIENCE_UPDATE_TIME_CN,
          };
        },
      },
    },
  },
  [DATASOURCE_KEY_MAP.GSD]: {
    [GSD_METRIC_KEY_MAP.TOTAL_UNITS]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GSD],
      metric: 'total_units',
      metricJump: 'Total Units',
      metricName: {
        en: 'Total Units',
        cn: '总销量',
      },
      explanation: {
        en: {
          definition: '',
          granularity: 'Weekly',
          aggregation: 'Sum',
          updateTime: '7-days delay, usually every Wednesday at 11AM UTC+0, you can see data up to 7 days ago.',
        },
        cn: {
          definition: '',
          granularity: '周',
          aggregation: '取总值',
          updateTime: '延时1周, 通常每周三的11AM UTC+0 可以看到日期截止1周前的数据。',
        },
      },
    },
    [GSD_METRIC_KEY_MAP.DIGITAL_UNITS]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GSD],
      metric: 'digital_units',
      metricJump: 'Digital Units',
      metricName: {
        en: 'Digital Units',
        cn: '线上销量',
      },
      explanation: {
        en: {
          definition:
            'The total number of digital units sold weekly, including full games and DLC. The pre-orders are calculated in the 1st week of game full launch or early access.',
          granularity: 'Weekly',
          aggregation: 'Sum',
          updateTime: '7-days delay, usually every Wednesday at 11AM UTC+0, you can see data up to 7 days ago.',
        },
        cn: {
          definition: '游戏电子版销量, 预购数据在游戏正式发布或抢先体验的第一周进行统计',
          granularity: '周',
          aggregation: '取总值',
          updateTime: '延时1周, 通常每周三的11AM UTC+0 可以看到日期截止1周前的数据。',
        },
      },
    },
    [GSD_METRIC_KEY_MAP.PHYSICAL_UNITS]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GSD],
      metric: 'physical_units',
      metricJump: 'Physical Units',
      metricName: {
        en: 'Physical Units',
        cn: '零售销量',
      },
      explanation: {
        en: {
          definition:
            'The total number of physical game units sold through retail channels. The pre-orders are calculated in the 1st week of game full launch or early access.',
          granularity: 'Weekly',
          aggregation: 'Sum',
          updateTime: '7-days delay, usually every Wednesday at 11AM UTC+0, you can see data up to 7 days ago.',
        },
        cn: {
          definition: '游戏实体版销量, 预购数据在游戏正式发布或抢先体验的第一周进行统计',
          granularity: '周',
          aggregation: '取总值',
          updateTime: '延时1周, 通常每周三的11AM UTC+0 可以看到日期截止1周前的数据。',
        },
      },
    },
    [GSD_METRIC_KEY_MAP.TOTAL_REVENUE]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GSD],
      metric: 'total_revenue',
      metricJump: 'Total Revenue',
      metricName: {
        en: 'Total Revenue(€)',
        cn: '总收入(€)',
      },
      explanation: {
        en: {
          definition: '',
          granularity: 'Weekly',
          aggregation: 'Sum',
          updateTime:
            '3-months delay, usually every Wednesday at 11AM UTC+0, you can see data up to 3 months ago. (The data undergoes a rolling update every week).',
        },
        cn: {
          definition: '',
          granularity: '周',
          aggregation: '取总值',
          updateTime: '延时3个月, 通常每周三的11AM UTC+0 可以看到日期截止3个月前的数据。(数据每周进行滚动更新)',
        },
      },
    },
    [GSD_METRIC_KEY_MAP.DIGITAL_REVENUE]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GSD],
      metric: 'digital_revenue',
      metricJump: 'Digital Revenue',
      metricName: {
        en: 'Digital Revenue(€)',
        cn: '线上收入(€)',
      },
      explanation: {
        en: {
          definition:
            'Total digital revenue, including refunds, giving a net view of earnings. The pre-orders are calculated in the 1st week of game full launch or early access.',
          granularity: 'Weekly',
          aggregation: 'Sum',
          updateTime:
            '3-months delay, usually every Wednesday at 11AM UTC+0, you can see data up to 3 months ago. (The data undergoes a rolling update every week).',
        },
        cn: {
          definition: '游戏电子版收入（考虑退款）, 预购数据在游戏正式发布或抢先体验的第一周进行统计。',
          granularity: '周',
          aggregation: '取总值',
          updateTime: '延时3个月, 通常每周三的11AM UTC+0 可以看到日期截止3个月前的数据。(数据每周进行滚动更新)',
        },
      },
    },
    [GSD_METRIC_KEY_MAP.PHYSICAL_REVENUE]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GSD],
      metric: 'physical_revenue',
      metricJump: 'Physical Revenue',
      metricName: {
        en: 'Physical Revenue(€)',
        cn: '零售收入(€)',
      },
      explanation: {
        en: {
          definition:
            'Total revenue from physical game sales, including refunds. The pre-orders are calculated in the 1st week of game full launch or early access.',
          granularity: 'Weekly',
          aggregation: 'Sum',
          updateTime: '7-days delay, usually every Wednesday at 11AM UTC+0, you can see data up to 7 days ago.',
        },
        cn: {
          definition: '游戏实体版收入（考虑退款）, 预购数据在游戏正式发布或抢先体验的第一周进行统计。',
          granularity: '周',
          aggregation: '取总值',
          updateTime: '延时1周, 通常每周三的11AM UTC+0 可以看到日期截止1周前的数据。',
        },
      },
    },
  },
  [DATASOURCE_KEY_MAP.NPD]: {
    [NPD_METRIC_KEY_MAP.PHYSICAL_REVENUE]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.NPD],
      metric: 'physical_revenue',
      metricJump: 'Physical Revenue',
      metricName: {
        en: 'Physical Revenue($)',
        cn: '零售收入($)',
      },
      explanation: {
        en: {
          definition: 'The total revenue from physical game sales.',
          granularity: 'Monthly',
          aggregation: 'Sum',
          updateTime:
            '1-month delay, and new data will be available at 26th of each month, you can see data up to 1 month ago.',
        },
        cn: {
          definition: '游戏实体版收入',
          granularity: '月',
          aggregation: '取总值',
          updateTime: '延时1个月，每月26日可以看到日期截止1个月前的数据。',
        },
      },
    },
    [NPD_METRIC_KEY_MAP.PHYSICAL_UNITS]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.NPD],
      metric: 'physical_units',
      metricJump: 'Physical Units',
      metricName: {
        en: 'Physical Units',
        cn: '零售销量',
      },
      explanation: {
        en: {
          definition: 'The total number of physical game units sold.',
          granularity: 'Monthly',
          aggregation: 'Sum',
          updateTime:
            '1-month delay, and new data will be available at 26th of each month, you can see data up to 1 month ago.',
        },
        cn: {
          definition: '游戏实体版销量',
          granularity: '月',
          aggregation: '取总值',
          updateTime: '延时1个月，每月26日可以看到日期截止1个月前的数据。',
        },
      },
    },
  },
  [DATASOURCE_KEY_MAP.NEWZOO]: {
    [NEWZOO_METRIC_KEY_MAP.TOTAL_REVENUE]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.NEWZOO],
      metric: 'total_revenue',
      metricJump: 'Total Revenue',
      metricName: {
        en: 'Total Revenue($)',
        cn: '总收入($)',
      },
      explanation: {
        en: {
          definition: '',
          granularity: '',
          aggregation: '',
          updateTime: '',
        },
        cn: {
          definition: '',
          granularity: '',
          aggregation: '',
          updateTime: '',
        },
      },
    },
    [NEWZOO_METRIC_KEY_MAP.TOTAL_PLAYERS]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.NEWZOO],
      metric: 'total_players',
      metricJump: 'Total Players',
      metricName: {
        en: 'Total Players',
        cn: '总玩家数量',
      },
      explanation: {
        en: {
          definition: '',
          granularity: '',
          aggregation: '',
          updateTime: '',
        },
        cn: {
          definition: '',
          granularity: '',
          aggregation: '',
          updateTime: '',
        },
      },
    },
  },
  [DATASOURCE_KEY_MAP.GAMALYTIC]: {
    [GAMALYTIC_METRIC_KEY_MAP.PREMIUM_REVENUE]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'premium_revenue',
      metricJump: 'Steam Revenue',
      metricName: {
        en: 'Steam Revenue($)',
        cn: 'Steam收入(美元)',
      },
      explanation: {
        en: {
          definition: 'Estimated revenue from Steam premium game sales.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Total value',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: 'Steam付费游戏销售收入估算。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '总值',
          updateTime: '每日更新。',
        },
      },
    },
    [GAMALYTIC_METRIC_KEY_MAP.CUMULATIVE_REVENUE]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'cumulative_revenue',
      metricJump: 'Cumulative Revenue',
      metricName: {
        en: 'Cumulative Revenue($)',
        cn: '累计收入(美元)',
      },
      explanation: {
        en: {
          definition: 'Cumulative revenue from Steam premium game sales.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Total value',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: 'Steam付费游戏累计销售收入。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '总值',
          updateTime: '每日更新。',
        },
      },
    },
    [GAMALYTIC_METRIC_KEY_MAP.UNITS]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'units',
      metricJump: 'Units',
      metricName: {
        en: 'Units',
        cn: '销量',
      },
      explanation: {
        en: {
          definition: 'Estimated units sold for Steam premium games.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Total value',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: 'Steam付费游戏销量估算。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '总值',
          updateTime: '每日更新。',
        },
      },
    },
    [GAMALYTIC_METRIC_KEY_MAP.CUMULATIVE_UNITS]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'cumulative_units',
      metricJump: 'Cumulative Units',
      metricName: {
        en: 'Cumulative Units',
        cn: '累计销量',
      },
      explanation: {
        en: {
          definition: 'Cumulative units sold for Steam premium games.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Total value',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: 'Steam付费游戏累计销量。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '总值',
          updateTime: '每日更新。',
        },
      },
    },
    [GAMALYTIC_METRIC_KEY_MAP.PCU]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'pcu',
      metricJump: 'PCU',
      metricName: {
        en: 'PCU',
        cn: 'PCU',
      },
      explanation: {
        en: {
          definition: 'Peak Concurrent Users - The highest number of players playing the game simultaneously.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Peak value',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: '最高同时在线人数 - 同时在线玩家人数的峰值。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '峰值',
          updateTime: '每日更新。',
        },
      },
    },
    [GAMALYTIC_METRIC_KEY_MAP.ACU]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'acu',
      metricJump: 'ACU',
      metricName: {
        en: 'ACU',
        cn: 'ACU',
      },
      explanation: {
        en: {
          definition: 'Average Concurrent Users - The average number of players playing the game simultaneously.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Average value',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: '平均同时在线人数 - 同时在线玩家人数的平均值。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '平均值',
          updateTime: '每日更新。',
        },
      },
    },
    [GAMALYTIC_METRIC_KEY_MAP.FOLLOWERS]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'followers',
      metricJump: 'Followers',
      metricName: {
        en: 'Followers',
        cn: '关注人数',
      },
      explanation: {
        en: {
          definition: 'Number of users following the game on Steam.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Total value',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: 'Steam上关注游戏的用户数量。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '总值',
          updateTime: '每日更新。',
        },
      },
    },
    [GAMALYTIC_METRIC_KEY_MAP.REVIEWS]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'reviews',
      metricJump: 'Reviews',
      metricName: {
        en: 'Reviews',
        cn: '评论数量',
      },
      explanation: {
        en: {
          definition: 'Number of user reviews for the game on Steam.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Total value',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: 'Steam上游戏的用户评论数量。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '总值',
          updateTime: '每日更新。',
        },
      },
    },
    [GAMALYTIC_METRIC_KEY_MAP.PRICE]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'price',
      metricJump: 'Price',
      metricName: {
        en: 'Price($)',
        cn: '价格',
      },
      explanation: {
        en: {
          definition: 'Current price of the game on Steam.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Current value',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: 'Steam上游戏的当前价格。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '当前值',
          updateTime: '每日更新。',
        },
      },
    },
    [GAMALYTIC_METRIC_KEY_MAP.LIFETIME_WEIGHTED_PRICE]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'lifetime_weighted_price',
      metricJump: 'Lifetime Weighted Price',
      metricName: {
        en: 'Lifetime Weighted Price($)',
        cn: '终身加权价格',
      },
      explanation: {
        en: {
          definition: 'Lifetime weighted average price considering discounts and promotions.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Weighted average',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: '考虑折扣和促销的终身加权平均价格。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '加权平均',
          updateTime: '每日更新。',
        },
      },
    },
    [GAMALYTIC_METRIC_KEY_MAP.DAILY_WISHLISTS]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'daily_wishlists',
      metricJump: 'Daily Wishlists',
      metricName: {
        en: 'Daily Wishlists',
        cn: '每日愿望单',
      },
      explanation: {
        en: {
          definition: 'Daily number of users adding the game to their Steam wishlist.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Total value',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: '每日将游戏添加到Steam愿望单的用户数量。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '总值',
          updateTime: '每日更新。',
        },
      },
    },
    [GAMALYTIC_METRIC_KEY_MAP.LIFETIME_WISHLISTS]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'lifetime_wishlists',
      metricJump: 'Lifetime Wishlists',
      metricName: {
        en: 'Lifetime Wishlists',
        cn: '终身愿望单',
      },
      explanation: {
        en: {
          definition: 'Total number of users who have added the game to their Steam wishlist.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Total value',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: '将游戏添加到Steam愿望单的累计用户数量。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '总值',
          updateTime: '每日更新。',
        },
      },
    },
    [GAMALYTIC_METRIC_KEY_MAP.PLAYERS_BY_COUNTRY]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'players_by_country',
      metricJump: 'Players by Country',
      metricName: {
        en: 'Players by Country',
        cn: '按国家玩家分布',
      },
      explanation: {
        en: {
          definition: 'Distribution of players by country/region.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Total value',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: '按国家/地区分布的玩家数量。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '总值',
          updateTime: '每日更新。',
        },
      },
    },
    [GAMALYTIC_METRIC_KEY_MAP.TOTAL_FOLLOWERS]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'total_followers',
      metricJump: 'Total Followers',
      metricName: {
        en: 'Total Followers',
        cn: '总关注数',
      },
      explanation: {
        en: {
          definition: 'Total number of users following the game.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Total value',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: '关注游戏的用户总数。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '总值',
          updateTime: '每日更新。',
        },
      },
    },
    [GAMALYTIC_METRIC_KEY_MAP.AVG_PLAYTIME]: {
      ...COMBINE_PLATFORM,
      ...COMBINE_DATA_SOURCES_MAPPING[DATASOURCE_KEY_MAP.GAMALYTIC],
      metric: 'avg_playtime',
      metricJump: 'Avg Playtime',
      metricName: {
        en: 'Avg. Playtime(mins)',
        cn: '平均游戏时长',
      },
      explanation: {
        en: {
          definition: 'Average playtime per user in minutes.',
          dataSource: 'Gamalytic',
          granularity: 'Daily',
          aggregation: 'Average value',
          updateTime: 'Updated daily.',
        },
        cn: {
          definition: '每位用户的平均游戏时长（分钟）。',
          dataSource: 'Gamalytic',
          granularity: '日',
          aggregation: '平均值',
          updateTime: '每日更新。',
        },
      },
    },
  },
};
